/**
 * customTiming.ts
 *
 * Custom timing API for the metrics that matter to the GenesisView merge:
 * - viewer.click → viewer.firstImagePainted (the latency commitment)
 * - viewer.modeSwitch.start → viewer.modeSwitch.complete
 * - viewer.copyEmbed.click → viewer.copyEmbed.complete
 *
 * Uses User Timing API (performance.mark + performance.measure) so the
 * timings show up in DevTools too.
 */

import { getApiBase } from '@services/api/apiConfig';

const TELEMETRY_ENDPOINT = '/api/telemetry/custom';

function isTelemetryEnabled(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_ENABLE_TELEMETRY === 'true';
}

export interface CustomTimingMetadata {
  cacheState?: 'HIT' | 'MISS' | 'STALE' | 'BYPASS' | 'unknown';
  authKind?: 'owner' | 'public';
  contentType?: 'image' | 'pdf';
  mode?: string;
  folderHash?: string; // hash of folder path for grouping (no PII)
}

/** Mark the start of a named span. */
export function markStart(name: string): void {
  if (typeof performance === 'undefined') return;
  try {
    performance.mark(`${name}.start`);
  } catch { /* */ }
}

/**
 * End a span and ship the duration to telemetry.
 * No-op if start mark is missing.
 */
export function markEnd(name: string, metadata?: CustomTimingMetadata): void {
  if (typeof performance === 'undefined') return;
  try {
    performance.mark(`${name}.end`);
    const measure = performance.measure(name, `${name}.start`, `${name}.end`);
    sendCustom({
      name,
      durationMs: Math.round(measure.duration),
      route: window.location.pathname,
      metadata,
    });
  } catch {
    // Missing start mark — silently skip
  }
}

interface CustomReport {
  name: string;
  durationMs: number;
  route: string;
  metadata?: CustomTimingMetadata;
}

function sendCustom(report: CustomReport): void {
  if (!isTelemetryEnabled()) return;
  const url = `${getApiBase()}${TELEMETRY_ENDPOINT}`;
  const body = JSON.stringify({
    ...report,
    ts: new Date().toISOString(),
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true });
    }
  } catch { /* silent */ }
}
